import { useEffect, useState } from 'react';

import { ArrowForwardIosSharp } from '@mui/icons-material';
import { Button } from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import MuiAccordionSummary, {
  AccordionSummaryProps
} from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import CustomContainer from 'components/CustomContainer';
import { currentRole } from 'pages/Login/state';
import { jwtData } from 'pages/OnBoarding/state';

import api from 'utils/api';
import { useConstants } from 'utils/hooks';
import useWidth from 'utils/useWidth';

import List from './list';
import Table from './Table';

function TabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0
  },
  '&:before': {
    display: 'none'
  }
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)'
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1)
  }
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)'
}));

export default function OnBoarding(props: any) {
  const { CONSTANTS } = useConstants();
  const [owners, setOwners]: [any, any] = useState([]);
  const [ownerId, setOwnerId]: [any, any] = useState(null);
  const [credentialsData, setCredentialsData] = useState([]);
  const navigate = useNavigate();
  const width = useWidth();
  const [expanded, setExpanded]: [any, any] = useState('');
  const tokenData: any = useRecoilValue(jwtData);
  const myCurrentRole: any = useRecoilValue(currentRole);

  useEffect(() => {
    getData('/admin/owners');
  }, [myCurrentRole]);

  useEffect(() => {
    const execute = async () => {
      if (expanded === 'CREDENTIALS') {
        try {
          const response = await api.get(
            `/admin/owners/${ownerId}/box/credentials`
          );
          setCredentialsData(response.data);
        } catch (error) {
          console.log(error);
        }
      }
    };

    execute();
  }, [expanded]);

  const getData = async (url: string) => {
    try {
      const response = await api.get(url);
      setOwners(response.data);
      if (response.data.length) {
        setOwnerId(response.data[0].id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const handleDownload = async () => {
    try {
      const res: any = await api.get(`/admin/owners/${ownerId}/sdk`, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Create a link element and trigger a download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(res.data);
      link.download = 'mesn-buoy-guest-sdk.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      alert('Success: Downloaded');
    } catch (err: any) {
      alert(`Error: ${err.response.data.message}`);
    }
  };

  const tableColumns: any = [
    {
      name: 'Box ID',
      selector: (obj: any) => obj.boxId
    },
    {
      name: 'Password',
      selector: (obj: any) => obj.password
    }
  ];

  const handleOwner = (event: React.SyntheticEvent, newValue: number) => {
    setOwnerId(newValue);
    setExpanded('');
  };

  const getTabs = () => {
    return owners.map((owner: any) => (
      <TabPanel
        value={ownerId}
        index={owner.id}
        key={owner.id}
        style={{
          flex: 1
        }}
      >
        {myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER ? (
          <Button
            type='button'
            variant='secondary'
            color='dark'
            onClick={handleDownload}
          >
            Download SDK
          </Button>
        ) : null}

        <div
          style={{
            marginTop: 10
          }}
        >
          <Accordion
            expanded={expanded === 'ON_BOARDING'}
            onChange={handleChange('ON_BOARDING')}
          >
            <AccordionSummary
              aria-controls='ON_BOARDING-content'
              id='ON_BOARDING-header'
            >
              <Typography variant='body_large_strong' color='blue.800'>
                OnBoarding
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {expanded === 'ON_BOARDING' ? (
                <List
                  owner={owner}
                  typePath='onboarding'
                  type='ON_BOARDING'
                  isAdmin={
                    myCurrentRole === CONSTANTS.ROLE.ROLE_ADMIN ||
                    myCurrentRole === CONSTANTS.ROLE.ROLE_SUB_ADMIN
                  }
                />
              ) : null}
            </AccordionDetails>
          </Accordion>
          {/* <Accordion
            expanded={expanded === 'BOX_DEPLOYMENT'}
            onChange={handleChange('BOX_DEPLOYMENT')}
          >
            <AccordionSummary
              aria-controls='BOX_DEPLOYMENT-content'
              id='BOX_DEPLOYMENT-header'
            >
              <Typography variant='body_large_strong' color='blue.800'>
                Box Deployment
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {expanded === 'BOX_DEPLOYMENT' ? (
                <List
                  owner={owner}
                  typePath='box/deployment'
                  type='BOX_DEPLOYMENT'
                  isAdmin={
                    myCurrentRole === CONSTANTS.ROLE.ROLE_ADMIN ||
                    myCurrentRole === CONSTANTS.ROLE.ROLE_SUB_ADMIN
                  }
                />
              ) : null}
            </AccordionDetails>
          </Accordion> */}
          {myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER ? (
            <Accordion
              expanded={expanded === 'CREDENTIALS'}
              onChange={handleChange('CREDENTIALS')}
            >
              <AccordionSummary
                aria-controls='CREDENTIALS-content'
                id='CREDENTIALS-header'
              >
                <Typography variant='body_large_strong' color='blue.800'>
                  Credentials
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {credentialsData.length ? (
                  <Table columns={tableColumns} data={credentialsData} />
                ) : (
                  'No data found'
                )}
              </AccordionDetails>
            </Accordion>
          ) : null}
        </div>
      </TabPanel>
    ));
  };

  return (
    <>
      <div style={{ height: 32 }} />
      <CustomContainer>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <Typography
            variant={
              ['mobile', 'tablet'].includes(width) ? 'body_large_strong' : 'h2'
            }
            color='blue.900'
          >
            Instrument Onboarding
          </Typography>
        </div>

        {owners.length && ownerId ? (
          <Box
            sx={{
              flexGrow: 1,
              bgcolor: 'background.paper',
              display: 'flex',
              height: 224
            }}
          >
            <Tabs
              orientation='vertical'
              variant='scrollable'
              value={ownerId}
              onChange={handleOwner}
              sx={{ borderRight: 1, borderColor: 'divider' }}
            >
              {owners.map((owner: any) => (
                <Tab
                  label={`Owner - ${owner.ownerIdStr}`}
                  id={`vertical-tab-${owner.id}`}
                  value={owner.id}
                  key={owner.id}
                  className={'onboarding-tab'}
                />
              ))}
            </Tabs>
            {getTabs()}
          </Box>
        ) : (
          getTabs()
        )}
      </CustomContainer>
    </>
  );
}
